import {Dashboard} from 'common/src/modules/dashboards/dashboards.types';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {MetricValue} from 'common/src/modules/analytics/analytics.types';
import _isNaN from 'lodash/isNaN';
import {inlineWidgetSchema} from '../../../copilotAnalytics.schemas';
import {AccountAnalyticsToolName} from '../accountAnalyticsAgent.enums';
import checkAccountWidgetExistsTool from './checkAccountWidgetExistsTool';
import createAccountDashboardTool from './createAccountDashboardTool';
import createAccountWidgetTool from './createAccountWidgetTool';
import highlightAccountWidgetTool from './highlightAccountWidgetTool';

const inputSchema = z.object({
  widget: inlineWidgetSchema
    .describe(
      'The inline widget configuration that should be persisted as an account dashboard widget. Must be taken directly from the output of an analytics tool (for example AccountTopPerforming).',
    )
    .nullable()
    .optional(),
});

function persistInlineWidgetToAccountDashboardTool(store: CLStore) {
  async function execute({
    input,
    toolUtils,
    callChain,
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const {widget} = input;

    if (!widget) {
      return toolError('Inline widget configuration is required to persist a widget.');
    }

    const {
      dashboardPage: {dashboards},
    } = store.getState();

    const writableDashboards = dashboards.filter((dashboard: Dashboard) => !dashboard.readOnly);

    const {
      content: {metrics: aiMetrics, dimensions, type, breakdownByTime, eventType, withTrends},
    } = widget;

    const metrics = aiMetrics as MetricValue[];
    const [dimension] = dimensions;

    const checkWidgetExists = checkAccountWidgetExistsTool(store);
    const highlightWidget = highlightAccountWidgetTool(store);

    const existsResult = await checkWidgetExists.execute({
      input: {
        dimension,
        metrics,
        breakdownByTime,
        type,
        eventType,
      },
      toolUtils,
      callChain,
    });

    const existsResultParsed = JSON.parse(existsResult.content);

    if (existsResultParsed.dashboardId && existsResultParsed.widgetId) {
      const dashboardId = existsResultParsed.dashboardId;
      const widgetId = existsResultParsed.widgetId;

      await highlightWidget.execute({
        input: {
          dashboardId,
          widgetId,
        },
        toolUtils,
        callChain,
      });

      return toolResult('Existing widget highlighted.');
    }

    let selectedDashboardId: number | null = null;

    if (!writableDashboards.length) {
      const createDashboard = createAccountDashboardTool(false);

      const createDashboardResult = await createDashboard.execute({
        input: {},
        toolUtils,
        callChain,
      });

      const createdDashboardId = Number(
        createDashboardResult.content.replace(/^\D*(\d+)\D*$/, '$1'),
      );

      if (_isNaN(createdDashboardId)) {
        return toolError('Failed to create a new dashboard for this account.');
      }

      selectedDashboardId = createdDashboardId;
    } else if (writableDashboards.length === 1) {
      selectedDashboardId = writableDashboards[0].id;
    } else {
      for (const dashboard of writableDashboards) {
        const shouldUseDashboard = await toolUtils.askUserConfirmation({
          title: 'Choose dashboard for this widget',
          text: `Do you want to add or update this widget in the account dashboard "${dashboard.name}"?`,
          okLabel: 'Yes, use this dashboard',
          cancelLabel: 'No, show another option',
        });

        if (shouldUseDashboard) {
          selectedDashboardId = dashboard.id;

          break;
        }
      }

      if (!selectedDashboardId) {
        return toolResult('No dashboard selected. Inline widget was not persisted.');
      }
    }

    const dashboardId = selectedDashboardId;

    const createWidget = createAccountWidgetTool(store);

    const createResult = await createWidget.execute({
      input: {
        dashboardId,
        dimension,
        metrics,
        breakdownByTime,
        type,
        eventType,
        withTrends,
      },
      toolUtils,
      callChain,
    });

    const widgetId = Number(JSON.parse(createResult.content));

    if (_isNaN(widgetId)) {
      return toolError('Failed to create dashboard widget from inline widget.');
    }

    await highlightWidget.execute({
      input: {
        dashboardId,
        widgetId,
      },
      toolUtils,
      callChain,
    });

    return toolResult('New widget created and highlighted.');
  }

  return tool({
    name: AccountAnalyticsToolName.PersistInlineWidgetToAccountDashboard,
    displayName: 'Persisting inline widget to account dashboard',
    description:
      'Given an inline widget configuration, checks if a matching account dashboard widget exists and highlights it, or creates a new widget and highlights it.',
    execute,
    inputSchema,
  });
}

export default persistInlineWidgetToAccountDashboardTool;
